import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import LogoFallback from "/assets/img/bubbanlogo.jpg";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";

export default function DbaPage({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Department of Business Administration | Bandarban University",
}) {
  // Update Browser Tab Title
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const rootStyle = {
    "--accent": accent,
    backgroundImage: `linear-gradient(135deg, var(--accent) 0%, rgba(17,94,50,0.85) 40%, rgba(255,255,255,0.03) 100%)`,
  };
	const [faculties, setFaculties] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const faculty = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				department_id: "2",
				designation_id: "0",
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },				
			});
			const response = await api.post("/teacher-list", payload);
			
			console.log(response.data);
			// Loop data (array)
			setFaculties(response.data || []);
			
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	
	useEffect(() => {
		faculty();
	}, []);
  return (
    <div>
      {/* Hero Section */}
		<section
        className="relative w-full overflow-hidden px-6 sm:px-8 lg:px-16"
        style={rootStyle}
		>
        <div className="relative max-w-7xl mx-auto flex flex-col items-center text-center py-20 lg:py-28">
          <img
            src={logoSrc}
            alt="Logo"
            className="w-28 h-28 object-contain drop-shadow-md mb-6"
          />

          <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
            <span style={{ color: "var(--accent)" }}>Welcome to</span> Department of Business Administration
          </h1>

          <p className="mt-4 max-w-2xl text-white/90 text-lg sm:text-xl">
            Building bright minds with a strong foundation in knowledge,
            innovation, and community.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={`${import.meta.env.VITE_API_SOURCE_URL}home/apply-online`}
              className="px-6 py-3 bg-white rounded-xl font-semibold text-sm shadow hover:opacity-95"
              style={{ color: "var(--accent)" }}
            >
              Apply Now
            </a>
            <a
              href="#about"
              className="px-6 py-3 border border-white/40 rounded-xl text-white text-sm hover:bg-white/10"
            >
              Learn More
            </a>
			</div>
		</div>
		
         {/* Decorative bottom wave */}
        <div className="-mt-6">
			<svg
				viewBox="0 0 1440 80"
				xmlns="http://www.w3.org/2000/svg"
				className="w-full h-20"
			>
            <path
              d="M0,32 C240,96 480,0 720,32 C960,64 1200,16 1440,32 L1440 80 L0 80 Z"
              fill="white"
              opacity="0.92"
            />
			</svg>
        </div>
		</section>
		<section className="max-w-6xl mx-auto py-16 px-6 grid md:grid-cols-2 gap-10 items-center">
		</section>
		
		{/* Faculty List */}
		<section className="max-w-7xl mx-auto py-12 px-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {faculties.map((faculty, i) => (
          <motion.div
            key={faculty.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition duration-300 overflow-hidden"
          >
            <img
              src={`${import.meta.env.VITE_API_SOURCE_URL}${faculty.picture ? faculty.picture : "assets/images/teacher.jpg"}`}
              alt={faculty.name}
              className="w-full h-56 object-cover"
            />
            <div className="p-5 flex-1 flex flex-col">
				<div className="space-y-2 flex-1">
				  <h3 className="text-xl font-semibold">{faculty.name}</h3>
				  <p className="text-gray-600">{faculty.designation_name}</p>
				  <p className="text-sm text-indigo-600 font-medium">
					{faculty.department_name}
				  </p>
				  <p className="text-sm text-gray-500">📞 {faculty.mobile_number}</p>
				  <p className="text-sm text-gray-500">✉️ {faculty.email}</p>
				</div>
				<Link
					to={`/profile/${faculty.teacher_id}`}
					className="block w-full mt-4 px-4 py-2 text-center bg-green-600 text-white font-medium rounded-xl hover:bg-orange-500 transition"
				>
                View Profile
              </Link>
            </div>
          </motion.div>
		))}
		</section>
		 {/* Contact Info */}
		<section className="bg-gradient-to-r from-teal-600 to-green-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-4">
          <h3 className="text-2xl font-bold mb-4">Office of the Department of Business Administration</h3>
          <div className="flex flex-col md:flex-row justify-center gap-6">
            <div className="flex items-center gap-2">
              <MapPin size={20} /> <span>Rowadaw Heights Main Road, Bandarban town</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={20} /> <span>+880 1318-305900</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={20} /> <span>dba@bubban.ac.bd</span>
            </div>
          </div>
          <button className="mt-6 px-6 py-3 bg-white text-teal-700 font-semibold rounded-lg shadow hover:bg-gray-100">
            Request Appointment
          </button>
        </div>
      </section>
	</div>
	);
}

DbaPage.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};

